<template>
  <div class="global-file-viewer">
    <MediaViewer
      v-if="isOwner && mediaIsOpen"
      :items="mediaItems"
      :index="mediaIndex"
      :hide-open-morphable="hideOpenMorphable"
      :hide-minimize="hideMinimize"
      v-on="forwardedListeners"
      @load="handleViewerLoad"
      @download="handleViewerDownload"
    />

    <DocumentViewer
      v-if="isOwner && documentItem"
      :item="documentItem"
      :revision-context="documentRevisionContext"
      :revision-loading="revisionLoading"
      v-on="forwardedListeners"
      @load="handleViewerLoad"
      @download="handleViewerDownload"
    />

    <ForgeFileViewer
      v-if="isOwner && forgeItem"
      :item="forgeItem"
      :revision-context="forgeRevisionContext"
      :revision-loading="revisionLoading"
      v-on="forwardedListeners"
      @load="handleViewerLoad"
      @download="handleViewerDownload"
    />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import MediaViewer from '@/components/Globals/Helpers/MediaViewer'
import DocumentViewer from '@/components/Globals/Helpers/DocumentViewer'
import ForgeFileViewer from '@/components/Globals/Helpers/ForgeFileViewer'
import { recordFileActivity } from '@/helpers/api/fileManager'
import {
  claimViewer,
  createViewerOwnerId,
  releaseViewer
} from '@/helpers/viewerCoordinator'

export default {
  name: 'GlobalFileViewer',

  components: {
    MediaViewer,
    DocumentViewer,
    ForgeFileViewer
  },

  props: {
    mediaItems: {
      type: Array,
      default: () => []
    },
    mediaIndex: {
      type: Number,
      default: null
    },
    documentItem: {
      type: Object,
      default: null
    },
    forgeItem: {
      type: Object,
      default: null
    },
    documentRevisionContext: {
      type: Object,
      default: null
    },
    forgeRevisionContext: {
      type: Object,
      default: null
    },
    revisionLoading: {
      type: Boolean,
      default: false
    },
    hideOpenMorphable: {
      type: Boolean,
      default: false
    },
    hideMinimize: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ownerId: createViewerOwnerId(),
      isOwner: false
    }
  },

  computed: {
    mediaIsOpen() {
      return this.mediaIndex !== null && this.mediaIndex !== undefined
    },
    forwardedListeners() {
      const listeners = { ...this.$listeners }
      delete listeners.load
      delete listeners.download
      return listeners
    }
  },

  watch: {
    documentItem: {
      immediate: true,
      handler(item) {
        this.removeMinimizedFile(item)
        this.syncOwnership()
      }
    },
    forgeItem: {
      immediate: true,
      handler(item) {
        this.removeMinimizedFile(item)
        this.syncOwnership()
      }
    },
    mediaItems: {
      handler() {
        this.removeOpenMediaFile()
        this.syncOwnership()
      }
    },
    mediaIndex: {
      handler() {
        this.removeOpenMediaFile()
        this.syncOwnership()
      }
    }
  },

  mounted() {
    this.syncOwnership()
  },

  beforeDestroy() {
    releaseViewer(this.ownerId)
  },

  methods: {
    handleViewerLoad(payload) {
      const item = payload?.item
      if (item?.id) {
        const action = payload.viewer === 'forge' ? 'file_forge_viewed' : 'file_viewed'
        recordFileActivity({
          file_id: item.id,
          action,
          client_event_id: uuidv4(),
          metadata: { viewer: payload.viewer || 'file_manager' }
        }).catch(() => {})
      }
      this.$emit('load', payload)
    },
    handleViewerDownload(payload) {
      const item = payload?.item
      if (item?.id) {
        recordFileActivity({
          file_id: item.id,
          action: 'file_download_requested',
          client_event_id: uuidv4(),
          metadata: { viewer: payload.viewer || 'file_manager' }
        }).catch(() => {})
      }
      this.$emit('download', payload)
    },
    hasViewerContent() {
      return this.mediaIsOpen || Boolean(this.documentItem) || Boolean(this.forgeItem)
    },
    syncOwnership() {
      if (!this.hasViewerContent()) {
        this.isOwner = false
        releaseViewer(this.ownerId)
        return
      }

      this.isOwner = claimViewer(this.ownerId, this.handlePreempted.bind(this))
    },
    handlePreempted() {
      this.isOwner = false
      this.$emit('close')
    },
    removeOpenMediaFile() {
      if (!this.mediaIsOpen) return
      const mediaItem = this.mediaItems[this.mediaIndex]
      this.removeMinimizedFile(mediaItem?.item || mediaItem)
    },
    removeMinimizedFile(item) {
      if (!item?.id || !this.$store?.globalStore) return
      this.$store.globalStore.commit('removeFileFromFileManagerHandler', item)
    }
  }
}
</script>
